/*
  Back-in-stock capture, two widgets sharing one look:

  1. The REPLACE widget (`[data-gw-notify]`): takes over the Add-to-cart
     button's own rectangle when the CURRENTLY SELECTED variant is sold out.
     Three steps: prompt -> channel -> field.
  2. The SECONDARY widget (`[data-gw-secondary]`): sits UNDER the real,
     still-working Add-to-cart button when that variant is in stock but some
     OTHER variant of the product is sold out. Four steps: prompt -> which
     size -> channel -> field. Lets the shopper pick which sold-out variant
     they want notified about, from a list rendered straight off
     `product.variants` in Liquid — not from Dawn's live picker state, which
     for a colour+size product is relative to whichever colour is currently
     selected and isn't a reliable source for "every sold-out variant".

  Whether the SELECTED variant is sold out is read from the LIVE Add-to-cart
  button rather than from Liquid. Two things change availability after the
  page renders — Dawn swapping variants, and the size gate holding the
  button — so a render-time answer would be wrong for exactly the shoppers
  this is for.

  The gate matters in the other direction too: while it is holding, the
  button is disabled and reads "Select a Size". That is NOT sold out, and
  showing a restock flow there would be nonsense, so the label is checked,
  not just `disabled`.
*/
(function () {
  if (window.gwNotifyBooted) return;
  window.gwNotifyBooted = true;

  var SOLD_OUT = ((window.variantStrings && window.variantStrings.soldOut) || 'Sold out')
    .trim().toLowerCase();
  var UNAVAILABLE = ((window.variantStrings && window.variantStrings.unavailable) || 'Unavailable')
    .trim().toLowerCase();

  function track(event, extra) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, extra || {}));
  }

  /* `.hidden` alone is not reliable on anything wearing Dawn's own
     product-form__submit/button classes (the prompt buttons deliberately do,
     so they match Add-to-cart exactly): that same class-selector CSS beats
     the UA [hidden] rule on specificity and wins on source order, so the
     element stays visually flexed even while .hidden reads true. An inline
     style always wins regardless. Used for every step toggle in both
     widgets, and for hiding the real buy area. */
  function toggle(el, show) {
    if (!el) return;
    el.hidden = !show;
    if (show) el.style.removeProperty('display');
    else el.style.setProperty('display', 'none', 'important');
  }

  /* Both widgets render as siblings of <product-form>, not inside
     .product-form__buttons — necessary for the replace-widget (it must sit
     where the whole product-form used to be) and just simplest for the
     secondary one. That container is NOT always the same width as its own
     parents: Dawn caps .product-form__buttons at a fixed max-width in a
     mid-size viewport band (measured: pinned at 440px at both 500px and
     585px windows, while <product-form> itself scales past 550px there) —
     a real, visible mismatch, not a rendering illusion, confirmed pixel-exact
     against a live screenshot. Mirroring the measured width directly sidesteps
     needing to know WHICH Dawn breakpoint rule causes it, and keeps working
     if that rule ever changes. Re-applied on resize since the cap is
     viewport-width-dependent. */
  function matchButtonsWidth(el, referenceButton) {
    function apply() {
      var wrap = referenceButton() && referenceButton().closest('.product-form__buttons');
      if (!wrap) return;
      el.style.width = wrap.getBoundingClientRect().width + 'px';
    }
    apply();
    window.addEventListener('resize', apply);
    return apply;
  }

  function boot(root) {
    if (root.dataset.gwBooted) return;
    root.dataset.gwBooted = '1';

    var scope = root.closest('product-info') || root.closest('.product') || document;

    var promptBtn = root.querySelector('[data-gw-step="prompt"]');
    var channelBox = root.querySelector('[data-gw-step="channel"]');
    var form = root.querySelector('[data-gw-step="field"]');
    var doneEl = root.querySelector('[data-gw-step="done"]');
    var backBtn = root.querySelector('.gw-nf__back');
    var emailRow = root.querySelector('.gw-nf__row--email');
    var phoneRow = root.querySelector('.gw-nf__row--phone');
    var email = root.querySelector('input[name="email"]');
    var phone = root.querySelector('input[name="phone"]');
    var consent = root.querySelector('input[name="smsConsent"]');
    var honey = root.querySelector('input[name="website"]');
    var goBtn = root.querySelector('.gw-nf__go');
    var err = root.querySelector('.gw-nf__err');
    /* Tracked per variant, not as one flag for the whole product: a shopper
       who signs up for a sold-out M and then browses to a sold-out L should
       still be asked about L, not shown a stale "you're on the list". */
    var sentVariantId = null;
    var channel = null;

    function addButton() { return scope.querySelector('[name="add"]'); }

    /* The whole buy area, not just the Add-to-cart button: Dawn also renders
       a Shop Pay button and a "More payment options" link as siblings of it
       inside <product-form>, and this is meant to REPLACE the entire thing,
       not sit below what's left of it. */
    function buyWrap() {
      var b = addButton();
      return b ? b.closest('product-form') : null;
    }

    function buttonLabel() {
      var b = addButton();
      if (!b) return '';
      var span = b.querySelector('span');
      return (span ? span.textContent : b.textContent || '').trim().toLowerCase();
    }

    function soldOut() {
      var b = addButton();
      if (!b || !b.disabled) return false;
      var label = buttonLabel();
      return label === SOLD_OUT || label === UNAVAILABLE;
    }

    function variantId() {
      var input = scope.querySelector('.product-variant-id');
      if (input && input.value) return String(input.value);
      var fromUrl = new URLSearchParams(window.location.search).get('variant');
      return fromUrl || '';
    }

    function variantTitle() {
      var picked = [];
      scope.querySelectorAll('fieldset').forEach(function (group) {
        var on = group.querySelector('input:checked');
        if (on && on.value) picked.push(on.value);
      });
      return picked.join(' / ');
    }

    function fail(message, field) {
      err.textContent = message;
      err.hidden = false;
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.focus();
      }
    }

    function clearErrors() {
      err.hidden = true;
      err.textContent = '';
      [email, phone].forEach(function (f) { f.removeAttribute('aria-invalid'); });
    }

    function setStep(step) {
      toggle(promptBtn, step === 'prompt');
      toggle(channelBox, step === 'channel');
      toggle(form, step === 'field');
      toggle(doneEl, step === 'done');
      if (step === 'field') {
        toggle(emailRow, channel === 'email');
        toggle(phoneRow, channel === 'phone');
        var toFocus = channel === 'phone' ? phone : email;
        if (toFocus) setTimeout(function () { toFocus.focus(); }, 0);
      }
    }

    promptBtn.addEventListener('click', function () {
      setStep('channel');
    });

    channelBox.querySelectorAll('[data-channel]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        channel = btn.getAttribute('data-channel');
        clearErrors();
        setStep('field');
        track('gw_notify_channel_chosen', { gwChannel: channel });
      });
    });

    backBtn.addEventListener('click', function () {
      clearErrors();
      setStep('channel');
    });

    /* `show` deliberately does NOT depend on whether this variant was
       already signed up for: once a shopper has signed up, the "you're on
       the list" message must keep replacing the button for as long as this
       variant stays sold out. Gating show on that made the success message
       flash and then hand the rectangle back to the real, disabled,
       dead-end "Sold out" button the instant the success DOM update itself
       woke the observer. */
    var shown = false;
    function sync() {
      var show = soldOut();
      if (show === shown) return;
      shown = show;
      /* Measure BEFORE hiding buyWrap below: the reference button lives
         inside it, and getBoundingClientRect() on anything behind a
         display:none ancestor reads 0 — reflow() after the hide would stamp
         an inline width:0, which is worse than never setting one. */
      if (show) reflow();
      toggle(root, show);
      toggle(buyWrap(), !show);
      if (show) {
        var vid = variantId();
        if (sentVariantId !== null && sentVariantId === vid) {
          setStep('done');
        } else {
          channel = null;
          clearErrors();
          setStep('prompt');
        }
        track('gw_notify_shown', { gwVariantId: vid });
      }
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearErrors();

      var emailValue = channel === 'email' ? email.value.trim() : '';
      var phoneValue = channel === 'phone' ? phone.value.trim() : '';

      if (channel === 'email' && !emailValue) {
        return fail('Add an email address.', email);
      }
      if (emailValue && emailValue.indexOf('@') < 1) {
        return fail('That email address does not look right.', email);
      }
      if (channel === 'phone' && !phoneValue) {
        return fail('Add a mobile number.', phone);
      }
      /* Refusing here rather than dropping the number silently: a shopper who
         typed one and was not texted would reasonably think we lost it. */
      if (phoneValue && !consent.checked) {
        return fail('Tick the box below so we are allowed to text you.', phone);
      }

      var id = variantId();
      if (!id) {
        return fail('Pick a size first, then we can watch that one for you.');
      }

      goBtn.disabled = true;
      var original = goBtn.textContent;
      goBtn.textContent = 'Sending...';

      fetch(root.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailValue,
          phone: phoneValue,
          smsConsent: !!(phoneValue && consent.checked),
          variantId: id,
          variantTitle: variantTitle(),
          productId: root.dataset.productId,
          productHandle: root.dataset.productHandle,
          productTitle: root.dataset.productTitle,
          sourceUrl: window.location.href,
          website: honey ? honey.value : ''
        })
      })
        .then(function (response) { return response.json().catch(function () { return {}; }); })
        .then(function (result) {
          if (!result || result.ok !== true) throw new Error((result && result.error) || 'failed');
          sentVariantId = id;
          doneEl.textContent = phoneValue
            ? 'You are on the list. We will text you when it is back.'
            : 'You are on the list. We will email you when it is back.';
          setStep('done');
          track('gw_notify_signup', { gwVariantId: id, gwChannel: channel });
        })
        .catch(function () {
          goBtn.disabled = false;
          goBtn.textContent = original;
          fail('That did not go through. Try again in a moment.');
        });
    });

    var reflow = matchButtonsWidth(root, addButton);

    /* The button's disabled attribute and its label both change in place, so
       attributes and text have to be watched, not just childList. */
    var observer = new MutationObserver(sync);
    observer.observe(scope === document ? document.body : scope, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled']
    });

    sync();
    /* The gate re-runs on a timer for its first few seconds; match it so the
       flow appears as soon as the button settles. */
    var ticks = 0;
    var timer = setInterval(function () {
      if (++ticks > 20) return clearInterval(timer);
      sync();
    }, 300);
  }

  function bootSecondary(root) {
    if (root.dataset.gwBooted) return;
    root.dataset.gwBooted = '1';

    var scope = root.closest('product-info') || root.closest('.product') || document;

    var promptBtn = root.querySelector('[data-gw2-step="prompt"]');
    var sizesBox = root.querySelector('[data-gw2-step="sizes"]');
    var channelBox = root.querySelector('[data-gw2-step="channel"]');
    var form = root.querySelector('[data-gw2-step="field"]');
    var doneEl = root.querySelector('[data-gw2-step="done"]');
    var backBtn = root.querySelector('.gw-nf__back');
    var emailRow = root.querySelector('.gw-nf__row--email');
    var phoneRow = root.querySelector('.gw-nf__row--phone');
    var email = root.querySelector('input[name="email"]');
    var phone = root.querySelector('input[name="phone"]');
    var consent = root.querySelector('input[name="smsConsent"]');
    var honey = root.querySelector('input[name="website"]');
    var goBtn = root.querySelector('.gw-nf__go');
    var err = root.querySelector('.gw-nf__err');
    var sizeChoices = root.querySelectorAll('[data-gw-variant-id]');

    if (!sizeChoices.length) return; /* nothing sold out at render time */

    var channel = null;
    var chosenVariantId = null;
    var chosenVariantTitle = null;
    var sentVariantId = null;

    function addButton() { return scope.querySelector('[name="add"]'); }

    function buttonLabel() {
      var b = addButton();
      if (!b) return '';
      var span = b.querySelector('span');
      return (span ? span.textContent : b.textContent || '').trim().toLowerCase();
    }

    /* Whether the CURRENTLY SELECTED variant (not the ones this widget lists)
       is sold out — when it is, the REPLACE widget above takes over the
       button and this one has nothing to add. */
    function selectedSoldOut() {
      var b = addButton();
      if (!b || !b.disabled) return false;
      var label = buttonLabel();
      return label === SOLD_OUT || label === UNAVAILABLE;
    }

    function fail(message, field) {
      err.textContent = message;
      err.hidden = false;
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.focus();
      }
    }

    function clearErrors() {
      err.hidden = true;
      err.textContent = '';
      [email, phone].forEach(function (f) { f.removeAttribute('aria-invalid'); });
    }

    function setStep(step) {
      toggle(promptBtn, step === 'prompt');
      toggle(sizesBox, step === 'sizes');
      toggle(channelBox, step === 'channel');
      toggle(form, step === 'field');
      toggle(doneEl, step === 'done');
      if (step === 'field') {
        toggle(emailRow, channel === 'email');
        toggle(phoneRow, channel === 'phone');
        var toFocus = channel === 'phone' ? phone : email;
        if (toFocus) setTimeout(function () { toFocus.focus(); }, 0);
      }
    }

    function pickVariant(id, title) {
      chosenVariantId = id;
      chosenVariantTitle = title;
      clearErrors();
      setStep('channel');
    }

    promptBtn.addEventListener('click', function () {
      /* Skip the picker entirely when there is only one sold-out variant —
         nothing to choose. */
      if (sizeChoices.length === 1) {
        var only = sizeChoices[0];
        pickVariant(only.getAttribute('data-gw-variant-id'), only.getAttribute('data-gw-variant-title'));
      } else {
        setStep('sizes');
      }
    });

    sizeChoices.forEach(function (btn) {
      btn.addEventListener('click', function () {
        pickVariant(btn.getAttribute('data-gw-variant-id'), btn.getAttribute('data-gw-variant-title'));
      });
    });

    channelBox.querySelectorAll('[data-channel]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        channel = btn.getAttribute('data-channel');
        clearErrors();
        setStep('field');
        track('gw_notify_channel_chosen', { gwChannel: channel, gwSecondary: true });
      });
    });

    backBtn.addEventListener('click', function () {
      clearErrors();
      setStep('channel');
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearErrors();

      var emailValue = channel === 'email' ? email.value.trim() : '';
      var phoneValue = channel === 'phone' ? phone.value.trim() : '';

      if (channel === 'email' && !emailValue) {
        return fail('Add an email address.', email);
      }
      if (emailValue && emailValue.indexOf('@') < 1) {
        return fail('That email address does not look right.', email);
      }
      if (channel === 'phone' && !phoneValue) {
        return fail('Add a mobile number.', phone);
      }
      if (phoneValue && !consent.checked) {
        return fail('Tick the box below so we are allowed to text you.', phone);
      }
      if (!chosenVariantId) {
        return fail('Pick a size first, then we can watch that one for you.');
      }

      goBtn.disabled = true;
      var original = goBtn.textContent;
      goBtn.textContent = 'Sending...';

      fetch(root.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailValue,
          phone: phoneValue,
          smsConsent: !!(phoneValue && consent.checked),
          variantId: chosenVariantId,
          variantTitle: chosenVariantTitle || '',
          productId: root.dataset.productId,
          productHandle: root.dataset.productHandle,
          productTitle: root.dataset.productTitle,
          sourceUrl: window.location.href,
          website: honey ? honey.value : ''
        })
      })
        .then(function (response) { return response.json().catch(function () { return {}; }); })
        .then(function (result) {
          if (!result || result.ok !== true) throw new Error((result && result.error) || 'failed');
          sentVariantId = chosenVariantId;
          doneEl.textContent = phoneValue
            ? 'You are on the list. We will text you when it is back.'
            : 'You are on the list. We will email you when it is back.';
          setStep('done');
          track('gw_notify_signup', { gwVariantId: chosenVariantId, gwChannel: channel, gwSecondary: true });
        })
        .catch(function () {
          goBtn.disabled = false;
          goBtn.textContent = original;
          fail('That did not go through. Try again in a moment.');
        });
    });

    /* Shown whenever the SELECTED variant is in stock (else the replace
       widget owns the button) — the sold-out list itself is fixed at render
       time, so nothing else needs rechecking each tick. */
    var shown = false;
    function sync() {
      var show = !selectedSoldOut();
      if (show === shown) return;
      shown = show;
      toggle(root, show);
      if (show) {
        reflow();
        if (sentVariantId !== null && sentVariantId === chosenVariantId) {
          setStep('done');
        } else {
          channel = null;
          chosenVariantId = null;
          chosenVariantTitle = null;
          clearErrors();
          setStep('prompt');
        }
      }
    }

    var reflow = matchButtonsWidth(root, addButton);

    var observer = new MutationObserver(sync);
    observer.observe(scope === document ? document.body : scope, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled']
    });

    sync();
    var ticks = 0;
    var timer = setInterval(function () {
      if (++ticks > 20) return clearInterval(timer);
      sync();
    }, 300);
  }

  function start() {
    document.querySelectorAll('[data-gw-notify]').forEach(boot);
    document.querySelectorAll('[data-gw-secondary]').forEach(bootSecondary);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', start);
})();
